"use client";

import { FirebaseApp, initializeApp, getApps } from "firebase/app";
import { Auth, getAuth, signInWithCustomToken, User } from "firebase/auth";
import { Database, getDatabase } from "firebase/database";
import { Messaging, getMessaging } from "firebase/messaging";
import { FIREBASE_APP_INSTANCE_NAMES } from "./constants";

/**
 * Firebase Client Configuration
 * Define configuration for each Firebase app instance
 * Add new instances here and they will automatically work throughout the app
 */
interface FirebaseClientConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  databaseURL: string;
}

const FIREBASE_CLIENT_CONFIG: Record<string, FirebaseClientConfig> = {
  [FIREBASE_APP_INSTANCE_NAMES.DEFAULT]: {
    apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY || '',
    authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN || '',
    projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID || '',
    storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET || '',
    messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID || '',
    appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID || '',
    databaseURL: process.env.NEXT_PUBLIC_FIREBASE_DATABASE_URL || '',
  },
  [FIREBASE_APP_INSTANCE_NAMES.ADMIN_SUPPORT]: {
    apiKey: process.env.NEXT_PUBLIC_FIREBASE_SUPPORT_API_KEY || '',
    authDomain: process.env.NEXT_PUBLIC_FIREBASE_SUPPORT_AUTH_DOMAIN || '',
    projectId: process.env.NEXT_PUBLIC_FIREBASE_SUPPORT_PROJECT_ID || '',
    storageBucket: process.env.NEXT_PUBLIC_FIREBASE_SUPPORT_STORAGE_BUCKET || '',
    messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_SUPPORT_MESSAGING_SENDER_ID || '',
    appId: process.env.NEXT_PUBLIC_FIREBASE_SUPPORT_APP_ID || '',
    databaseURL: process.env.NEXT_PUBLIC_FIREBASE_SUPPORT_DATABASE_URL || '',
  }
};

/**
 * Initialize or get existing Firebase app instance
 * Uses configuration object to manage multiple Firebase projects
 * 
 * @param appName - Name of the Firebase app instance (default: [DEFAULT])
 * @returns FirebaseApp instance
 */
export const getFirebaseApp = (appName: string = FIREBASE_APP_INSTANCE_NAMES.DEFAULT): FirebaseApp => {
  // Check if the specific app instance already exists
  if (getApps().length > 0) {
    const existingApp = getApps().find(app => app?.name === appName);
    if (existingApp) {
      console.log(`Firebase Client: Using existing app instance '${appName}'`);
      return existingApp;
    }
  }

  // Get configuration for this app instance
  const appConfig = FIREBASE_CLIENT_CONFIG[appName];
  if (!appConfig) {
    throw new Error(`Firebase Client configuration not found for app: ${appName}. Add it to FIREBASE_CLIENT_CONFIG.`);
  }

  if (!appConfig.apiKey || !appConfig.authDomain || !appConfig.projectId || !appConfig.storageBucket || !appConfig.messagingSenderId || !appConfig.appId || !appConfig.databaseURL) {
    throw new Error(`Missing Firebase Client configuration for app: ${appName}. Check environment variables.`);
  }
  console.log(`Firebase Client: Initializing new app instance '${appName}'`);
  // Initialize with app name to create separate instance
  const newApp = initializeApp(appConfig, appName);
  console.log(`Firebase Client: Successfully initialized app '${appName}'`);
  return newApp;
};

/**
 * Get Firebase Auth instance
 * 
 * @param appName - Name of the Firebase app instance (default: [DEFAULT])
 * @returns Auth instance
 */
export const getFirebaseAuth = (appName: string = FIREBASE_APP_INSTANCE_NAMES.DEFAULT): Auth => {
  const app = getFirebaseApp(appName);
  return getAuth(app);
};

/**
 * Get Firebase Realtime Database instance
 * 
 * @param appName - Name of the Firebase app instance (default: [DEFAULT])
 * @returns Database instance
 */
export const getFirebaseDatabase = (appName: string = FIREBASE_APP_INSTANCE_NAMES.DEFAULT): Database => {
  const app = getFirebaseApp(appName);
  return getDatabase(app);
};

/**
 * Get Firebase Messaging instance
 * 
 * @param appName - Name of the Firebase app instance (default: [DEFAULT])
 * @returns Messaging instance
 */
export const getFirebaseMessaging = (appName: string = FIREBASE_APP_INSTANCE_NAMES.DEFAULT): Messaging => {
  const app = getFirebaseApp(appName);
  return getMessaging(app);
};

/**
 * Sign in with Firebase custom token
 * 
 * @param token - Custom Firebase token
 * @param appName - Name of the Firebase app instance (default: [DEFAULT])
 * @returns User object
 */
export const signInWithFirebaseToken = async (token: string, appName: string = FIREBASE_APP_INSTANCE_NAMES.DEFAULT): Promise<User> => {
  const auth = getFirebaseAuth(appName);
  const userCredential = await signInWithCustomToken(auth, token);
  return userCredential.user;
};

/**
 * Sanitize ID for Firebase path (remove invalid characters)
 * 
 * @param id - ID to sanitize
 * @returns Sanitized ID
 */
export const sanitizeFirebaseId = (id: string): string => {
  return id.replace(/[.#$\\[\\]]/g, '_');
};

/**
 * Check if Firebase is initialized
 * 
 * @returns boolean indicating if any Firebase app is initialized
 */
export const isFirebaseInitialized = (): boolean => {
  return getApps().length > 0;
};

/**
 * Check if a specific Firebase app instance is initialized
 * 
 * @param appName - Name of the Firebase app instance
 * @returns boolean indicating if the specific app instance is initialized
 */
export const isFirebaseAppInitialized = (appName: string): boolean => {
  return getApps().some(app => app?.name === appName);
};